import React, { useMemo } from "react";
import {
  isCzechHoliday,
  isBigShopClosedHoliday,
} from "../../utils/czechHolidays";

function isLikelyMallBranch(openingHours) {
  if (!openingHours || typeof openingHours !== "object") return false;

  const sat = openingHours?.["6"];
  const sun = openingHours?.["7"];

  const satOpen = !!(sat && sat.open && sat.close);
  const sunOpen = !!(sun && sun.open && sun.close);

  return satOpen || sunOpen;
}

function isHolidayOpenForBranch(isoDate, openingHours) {
  if (!isCzechHoliday(isoDate)) return true;

  const mallLike = isLikelyMallBranch(openingHours);

  // běžná pobočka = všechny svátky zavřeno
  if (!mallLike) return false;

  // mall / víkendová pobočka = zavřeno jen při retail closed svátcích
  return !isBigShopClosedHoliday(isoDate);
}

function formatISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function countWorkingDays(year, month, openingHours) {
  const jsMonth = month - 1;
  const lastDay = new Date(year, jsMonth + 1, 0).getDate();

  let count = 0;

  for (let day = 1; day <= lastDay; day++) {
    const d = new Date(year, jsMonth, day);
    const dow = d.getDay();

    if (dow === 0 || dow === 6) continue;

    const iso = formatISO(d);

    if (isCzechHoliday(iso) && !isHolidayOpenForBranch(iso, openingHours)) {
      continue;
    }

    count++;
  }

  return count;
}

function timeToMinutes(t) {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}

const dayKeyMap = {
  1: "1",
  2: "2",
  3: "3",
  4: "4",
  5: "5",
  6: "6",
  0: "7",
};

function getOpenHoursForDate(openingHours, date) {
  if (!openingHours) return null;
  const key = dayKeyMap[date.getDay()];
  const oh = openingHours?.[key];
  if (!oh || !oh.open || !oh.close) return 0;

  const openMin = timeToMinutes(oh.open);
  const closeMin = timeToMinutes(oh.close);
  if (closeMin <= openMin) return 0;

  return (closeMin - openMin) / 60;
}

function formatSmartHours(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0";

  const int = Math.round(num);
  if (Math.abs(num - int) < 1e-9) return String(int);

  const rounded2 = Math.round(num * 100) / 100;
  return rounded2
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.[0-9])0$/, "$1");
}

export default function EmployeeStatsPanel({
  employees,
  shifts,
  year,
  month,
  openingHours,
  monthlyAbsenceByDate = {},
}) {

  const stats = useMemo(() => {
    if (!year || !month) return [];

    const workingDays = countWorkingDays(year, month, openingHours);
    const jsMonth = month - 1;

    return employees.map((emp) => {
      const dailyFond = Number(emp.fullFond || 0);
      const monthFond = dailyFond * workingDays;

      let assignedShifts = 0;
      let assignedAbsence = 0;
      let assignedHoliday = 0;

      const daysInMonth = new Date(year, month, 0).getDate();

      for (let day = 1; day <= daysInMonth; day++) {
        const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const d = new Date(year, jsMonth, day);

        const localEmpShift = shifts?.[iso]?.employees?.[emp.id] || null;

        const addHoursByType = (type, explicitHours) => {
          if (type === "R" || type === "O" || type === "?") {
            if (typeof explicitHours === "number") assignedShifts += explicitHours;
            else assignedShifts += dailyFond;
            return;
          }

          if (type === "C") {
            const openHours = getOpenHoursForDate(openingHours, d);
            assignedShifts += typeof openHours === "number" ? openHours : dailyFond;
          }
        };

        // 1) lokální směna
        if (localEmpShift?.type) {
          addHoursByType(localEmpShift.type, localEmpShift.hours);
          continue;
        }

        // 2) směna jinde
        const elsewhere = emp?.scheduleByDate?.[iso] || null;
        if (elsewhere) {
          const pick = Array.isArray(elsewhere) ? elsewhere[0] : elsewhere;
          const t = pick && typeof pick === "object" ? pick.type : null;

          if (t) {
            addHoursByType(t, undefined);
            continue;
          }
        }

        // 3) schválená absence = započtené hodiny do fondu, ne odpracované
        const row = monthlyAbsenceByDate?.[iso]?.[emp.id];
        if (row) {
          const h = Number(row.hours || 0);
          if (Number.isFinite(h) && h > 0) assignedAbsence += h;
          continue;
        }

        // 4) svátek + zavřená pobočka = info, ale NE do assignedTotal
        const isHoliday = isCzechHoliday(iso);
        const holidayOpen = isHolidayOpenForBranch(iso, openingHours);

        if (isHoliday && !holidayOpen) {
          assignedHoliday += dailyFond;
        }
      }

      const assignedTotal = assignedShifts + assignedAbsence;

      return {
        ...emp,
        dailyFond,
        monthFond,
        assignedShifts,
        assignedAbsence,
        assignedHoliday,
        assignedTotal,
        diff: assignedTotal - monthFond,
      };
    });
  }, [
    employees,
    shifts,
    year,
    month,
    openingHours,
    monthlyAbsenceByDate,
  ]);

  return (
    <div className="border rounded-xl bg-white shadow-sm p-4">

      <div className="font-semibold text-gray-800 mb-2 text-sm">
        Přehled zaměstnanců
      </div>

      <div className="text-xs text-gray-500 mb-3">
        Měsíční fond = počet pracovních dní × denní fond
      </div>

      <div className="space-y-3 max-h-[900px] overflow-auto pr-1">

        {stats.map((emp) => {

          const isOver = emp.diff > 0;
          const isUnder = emp.diff < 0;

          return (
            <div
              key={emp.id}
              className="border rounded-lg p-2.5 text-sm bg-gray-50"
            >

              <div className="flex items-center justify-between mb-1">

                <div className="font-medium text-gray-800">
                  {emp.name}
                </div>

                <div className="text-xs uppercase text-gray-500">
                  {emp.role === "magistr"
                    ? "MAGISTR"
                    : emp.role === "assistant"
                    ? "ASISTENT"
                    : "SANITÁŘ"}
                </div>

              </div>

              <div className="text-xs text-gray-600">
                Denní fond:
                <span className="font-medium">
                  {" "} {emp.dailyFond} h
                </span>
              </div>

              <div className="text-xs text-gray-600">
                Měsíční fond:
                <span className="font-medium">
                  {" "} {emp.monthFond} h
                </span>
              </div>

              <div className="text-xs text-gray-600">
                Rozplánováno (směny):
                <span className="font-medium">
                  {" "} {emp.assignedShifts} h
                </span>
              </div>

              <div className="text-xs text-gray-600">
                Rozplánováno (absence):
                <span className="font-medium">
                  {" "} {emp.assignedAbsence} h
                </span>
              </div>

              <div className="text-xs text-gray-600">
                Svátky:
                <span className="font-medium">
                  {" "} {emp.assignedHoliday} h
                </span>
              </div>

              <div className="text-xs text-gray-600">
                Rozplánováno (celkem):
                <span className="font-medium">
                  {" "} {emp.assignedTotal} h
                </span>
              </div>

              <div className="text-xs mt-1">
                Zbývá / přebytek:
                {" "}

                <span
                  className={`font-semibold ${
                    isUnder
                      ? "text-red-600"
                      : isOver
                      ? "text-green-700"
                      : "text-gray-700"
                  }`}
                >
                  {emp.diff > 0 ? "+" : ""}
                  {formatSmartHours(emp.diff)} h
                </span>

              </div>

            </div>
          );
        })}

      </div>

    </div>
  );
}